<script setup lang="ts">
  import { computed } from 'vue'
  import { useTenantUserDetailContext } from '../../composables/useTenantUserDetail'
  import {
    useTenantUserAccessRolesUtils,
    type AgencyStructureWithAccessRoles
  } from '../../composables/useTenantUserAccessRolesUtils'
  import { useTenantUserRoleAssignments } from '../providers'
  import { useAgencyStructureContext } from '@gat-ship/agency'
  import { useAccessRolesCollectionContext } from '@gat-ship/access-control'
  import { IconPlus } from '@gat-ship/icons'
  import {
    Button,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel
  } from '@gat-ship/vue-components'

  defineProps<{
    agencyNodeId: string
  }>()

  const { roleAssignments, commands } = useTenantUserRoleAssignments()
  const { TenantUserAccessRolesUtils } = useTenantUserAccessRolesUtils()
  const agencyStructureDataSource = useAgencyStructureContext()!
  const accessRolesDataSource = useAccessRolesCollectionContext()!

  const agencyStructureWithAccessRoles = computed(
    (): AgencyStructureWithAccessRoles[] => {
      return TenantUserAccessRolesUtils.createAgencyStructureWithAccessRoles(
        agencyStructureDataSource.agencyStructure.value,
        accessRolesDataSource.accessRoles.value,
        roleAssignments.value
      )
    }
  )

  const tenantUserProvider = useTenantUserDetailContext()!

  const tenantUser = computed(() => {
    return tenantUserProvider.tenantUser.value
  })

  function getItems(agencyNodeId: string) {
    const assignableRoles =
      TenantUserAccessRolesUtils.selectAssignableAccessRoles(
        agencyStructureWithAccessRoles.value,
        agencyNodeId,
        accessRolesDataSource.accessRoles.value
      )

    const tenantUserId = tenantUser.value?.userId
    if (!tenantUserId) {
      return []
    }
    return assignableRoles.map((role, index) => ({
      title: index === 0 ? 'Add role' : undefined,
      label: role.name,
      icon: IconPlus,
      onClick: () => {
        commands.addRoleAssignment({
          userId: tenantUserId,
          payload: {
            roleId: role.id,
            agencyNodeId: agencyNodeId
          },
          commandId: ''
        })
      }
    }))
  }
</script>

<template>
  <DropdownMenu v-if="getItems(agencyNodeId).length > 0">
    <DropdownMenuTrigger as-child>
      <Button variant="link">Assign Role</Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <template v-for="item in getItems(agencyNodeId)" :key="item.label">
        <DropdownMenuLabel
          v-if="item.title"
          class="text-foreground mt-2 py-0 text-xs font-medium"
        >
          {{ item.title }}
        </DropdownMenuLabel>
        <DropdownMenuItem @click="item.onClick">
          <component :is="item.icon" v-if="item.icon" class="size-4" />
          {{ item.label }}
        </DropdownMenuItem>
      </template>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
